// Загрузка и сохранение документа тарифов — общее для двух вкладок админки.
//
// Вкладка «Тарифы» правит структуру и русский текст, вкладка «Переводы» —
// остальные языки. Документ у них один, поэтому один и тот же код загрузки,
// сохранения и отката живёт здесь, а не копией в каждой панели.
import { useCallback, useEffect, useState } from 'react';
import { useT } from '../i18n';
import type { Translate } from '../i18n';
import { errorKey, type ApiError } from '../auth';
import { fetchAdminPricing, saveAdminPricing, type PricingDoc } from '../pricing';

export type DocStatus = 'loading' | 'ready' | 'saving';

export interface PricingDocState {
  doc: PricingDoc | null;
  status: DocStatus;
  error: string;
  /** Показывается после удачного сохранения и гаснет на первой же правке. */
  saved: boolean;
  updatedAt: number | null;
  updatedBy: string;
  /** Правка черновика: на сервер ничего не уходит до save(). */
  patch(next: Partial<PricingDoc>): void;
  /** Замена черновика целиком — для правок, которые считают весь документ. */
  replace(next: PricingDoc): void;
  save(): Promise<void>;
  /** Откат к встроенному содержимому: сервер перезапишет базу засевом. */
  reset(): Promise<void>;
}

function message(t: Translate, error: ApiError): string {
  return error.message || t(errorKey(error));
}

export function usePricingDoc(): PricingDocState {
  const t = useT();
  const [doc, setDoc] = useState<PricingDoc | null>(null);
  const [status, setStatus] = useState<DocStatus>('loading');
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [updatedBy, setUpdatedBy] = useState('');

  const load = useCallback(async () => {
    const res = await fetchAdminPricing();
    if (!res.ok) {
      setError(message(t, res.error));
      setStatus('ready');
      return;
    }
    // Пустого документа сервер не отдаёт: база засеяна при первом запуске.
    // null здесь означал бы поломку засева, и показать тогда нечего.
    setDoc(res.value.doc);
    setUpdatedAt(res.value.updatedAt);
    setUpdatedBy(res.value.updatedBy);
    setError('');
    setStatus('ready');
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const patch = useCallback((next: Partial<PricingDoc>) => {
    setDoc((current) => (current ? { ...current, ...next } : current));
    setSaved(false);
  }, []);

  const replace = useCallback((next: PricingDoc) => {
    setDoc(next);
    setSaved(false);
  }, []);

  const send = useCallback(
    async (payload: PricingDoc | null) => {
      setStatus('saving');
      const res = await saveAdminPricing(payload);
      if (!res.ok) {
        setError(message(t, res.error));
        setStatus('ready');
        return;
      }
      setDoc(res.value.doc);
      setUpdatedAt(res.value.updatedAt);
      setUpdatedBy(res.value.updatedBy);
      setError('');
      setSaved(true);
      setStatus('ready');
    },
    [t],
  );

  const save = useCallback(async () => {
    if (doc) await send(doc);
  }, [doc, send]);

  const reset = useCallback(async () => {
    await send(null);
  }, [send]);

  return { doc, status, error, saved, updatedAt, updatedBy, patch, replace, save, reset };
}
